/*
 * Shared Schema.org identity, value and HTML serialization rules for exported pages and detail bundles.
 * All public entity IDs use production canonical page URLs; bad feed values are omitted.
 * JSON-LD is placed on its own source lines without changing the graph payload.
 * Homepages identify the commercial organization as publisher and link its existing brand.
 */
const SITE = 'https://www.royallepagecommercial.com';
const SCRIPT_SELECTOR = 'script[type="application/ld+json"]';
const PLACEHOLDER = /^(?:undefined|null|n\/?a|not available|unknown|none|-)$/i;

function text(value) {
  if (value === null || value === undefined) { return undefined; }
  const cleaned = String(value).trim();
  return cleaned && !PLACEHOLDER.test(cleaned) ? cleaned : undefined;
}

function number(value, min = -Infinity, max = Infinity) {
  if (value === null || value === undefined || value === '') { return undefined; }
  const parsed = typeof value === 'number' ? value : Number(String(value).trim());
  return Number.isFinite(parsed) && parsed >= min && parsed <= max ? parsed : undefined;
}

function pageUrl(path) {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) { return undefined; }
  try {
    const url = new URL(path, SITE);
    if (url.origin !== SITE || url.username || url.password || url.hash || url.search) { return undefined; }
    return url.href;
  } catch { return undefined; }
}

function entityId(path, fragment) {
  const url = pageUrl(path);
  return url ? `${url}#${fragment}` : undefined;
}

const ids = {
  website: () => `${SITE}/#website`,
  brand: () => `${SITE}/#brand`,
  organization: () => `${SITE}/#organization`,
  webpage: path => entityId(path, 'webpage'),
  breadcrumb: path => entityId(path, 'breadcrumb'),
  listing: path => entityId(path, 'listing'),
  property: path => entityId(path, 'property'),
  offer: (path, kind, dual = false) => entityId(path, dual ? `offer-${kind}` : 'offer'),
  person: englishPath => entityId(englishPath, 'person'),
  office: englishPath => entityId(englishPath, 'office')
};

function postalAddress(record) {
  const street = text(record?.address);
  const validStreet = street && !/^(?:confidential|undisclosed|address withheld|no address)$/i.test(street);
  const address = {
    '@type': 'PostalAddress',
    ...(validStreet ? { streetAddress: street } : {}),
    ...(text(record?.city) ? { addressLocality: text(record.city) } : {}),
    ...(text(record?.province) ? { addressRegion: text(record.province) } : {}),
    ...(text(record?.postal_code) ? { postalCode: text(record.postal_code) } : {})
  };
  return Object.keys(address).length > 1 ? address : undefined;
}

function geo(record) {
  const latitude = number(record?.latitude, -90, 90);
  const longitude = number(record?.longitude, -180, 180);
  if (latitude === undefined || longitude === undefined || (latitude === 0 && longitude === 0)) { return undefined; }
  return { '@type': 'GeoCoordinates', latitude, longitude };
}

function serialize(graph) {
  return JSON.stringify(graph).replace(/[<>&\u2028\u2029]/g, character =>
    `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`);
}

function writeGraph(document, graph) {
  document.querySelectorAll(SCRIPT_SELECTOR).forEach(script => {
    if (script.previousSibling?.nodeType === 3 && script.previousSibling.textContent === '\n') { script.previousSibling.remove(); }
    if (script.nextSibling?.nodeType === 3 && script.nextSibling.textContent === '\n') { script.nextSibling.remove(); }
    script.remove();
  });
  if (!graph) { return; }
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = `\n${serialize(graph)}\n`;
  document.head.append('\n', script, '\n');
}

function websiteGraph() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      { '@type': 'WebSite', '@id': ids.website(), url: `${SITE}/`, name: 'Royal LePage Commercial',
        inLanguage: ['en-CA', 'fr-CA'], publisher: { '@id': ids.organization() } },
      { '@type': 'Brand', '@id': ids.brand(), url: `${SITE}/`, name: 'Royal LePage Commercial' },
      { '@type': 'Organization', '@id': ids.organization(), url: `${SITE}/`, name: 'Royal LePage Commercial',
        logo: `${SITE}/images/RLP-Commercial-ReverseLogo-RGB.png`, brand: { '@id': ids.brand() } }
    ]
  };
}

module.exports = { SITE, ids, text, number, pageUrl, postalAddress, geo, serialize, writeGraph, websiteGraph };
